// Read-only Recurring (OrderSchedule) view for the partner-viewer portal.
//
// A partner viewer can see their partner's recurring schedules but not
// edit them (they can still cancel individual materialized orders from
// the dashboard — that's a separate, scoped action). Data comes from the
// JWT-scoped `GET /partners/me/schedules` endpoint. Plain read-only
// table — no Add / Discover / Run-materialization toolbar, no row
// actions — distinct from the admin PartnerSchedules component.
//
// #169 / #170 — the box and the stop position, read-only, as soon as one of
// the partner's tasks carries them.

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Segmented, Space, Table, Tag, Tooltip, message } from 'antd';
import { UnorderedListOutlined, CalendarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { getMySchedules } from '../modules/order-schedules/api';
import RecurringWeeklyGrid from '../components/RecurringWeeklyGrid';
import { boxLabel, byPosition } from '../utils/scheduleBox';

const DAY_LABELS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

function WeekdayDisplay({ weekdays = [] }) {
  return (
    <div style={{ display: 'flex', gap: 3 }}>
      {DAY_LABELS.map((label, i) => (
        <span
          key={i}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 22,
            height: 22,
            borderRadius: 4,
            fontSize: 11,
            fontWeight: 600,
            background: weekdays[i] ? 'var(--accent-strong)' : '#f0f0f0',
            color: weekdays[i] ? '#fff' : '#bbb',
          }}
        >
          {label}
        </span>
      ))}
    </div>
  );
}

export default function PartnerSchedulesReadOnly() {
  const { t: tPartners } = useTranslation('partners');
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  // URL-synced view mode + pickup/delivery filter (shareable links).
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view') === 'byday' ? 'byday' : 'list';
  const showParam = searchParams.get('show');
  const showPickups = showParam !== 'delivery' && showParam !== 'none';
  const showDeliveries = showParam !== 'pickup' && showParam !== 'none';
  const mergeParams = (mut) =>
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        mut(p);
        return p;
      },
      { replace: true },
    );
  const setView = (v) => mergeParams((p) => (v === 'byday' ? p.set('view', 'byday') : p.delete('view')));
  const setShow = (pick, deliv) =>
    mergeParams((p) => {
      const val = pick && deliv ? null : pick ? 'pickup' : deliv ? 'delivery' : 'none';
      if (val) p.set('show', val);
      else p.delete('show');
    });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { response, error } = await getMySchedules();
        if (error) throw new Error(error.message || 'Failed to load schedules');
        if (!cancelled) setSchedules(Array.isArray(response) ? response : []);
      } catch (e) {
        if (!cancelled) message.error(e?.message || 'Failed to load schedules');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const columns = [
    {
      title: 'Pickup site',
      key: 'pickupSite',
      render: (_, r) => r.pickupSite?.name || '—',
    },
    ...(schedules.some((r) => r.collectionPoint)
      ? [
          {
            title: tPartners('recurring.box'),
            key: 'box',
            render: (_, r) => (r.collectionPoint ? boxLabel(r.collectionPoint) : '—'),
          },
        ]
      : []),
    ...(schedules.some((r) => r.position != null)
      ? [
          {
            title: (
              <Tooltip title={tPartners('recurring.positionColumnTooltip')}>{tPartners('recurring.position')}</Tooltip>
            ),
            key: 'position',
            sorter: byPosition,
            render: (_, r) => (
              <Space size={4} wrap>
                <strong>{r.position ?? '—'}</strong>
                {(r.defaultTypes || []).map((tag) => (
                  <Tag key={tag} style={{ fontSize: 11, marginInlineEnd: 0 }}>
                    {tag}
                  </Tag>
                ))}
              </Space>
            ),
          },
        ]
      : []),
    {
      title: 'Delivery site',
      key: 'deliverySite',
      render: (_, r) => r.deliverySite?.name || '—',
    },
    {
      title: 'Days',
      key: 'weekdays',
      render: (_, r) => <WeekdayDisplay weekdays={r.weekdays} />,
    },
    {
      title: 'Window',
      key: 'window',
      render: (_, r) => {
        const s = r.deliveryTimeStart;
        const e = r.deliveryTimeEnd;
        return s && e ? `${s}–${e}` : s ? `${s}–…` : e ? `…–${e}` : '—';
      },
    },
    {
      title: 'Date range',
      key: 'dateRange',
      render: (_, r) => {
        const start = r.startDate ? dayjs(r.startDate).format('DD MMM YYYY') : '?';
        const end = r.endDate ? dayjs(r.endDate).format('DD MMM YYYY') : 'no end';
        return `${start} → ${end}`;
      },
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, r) => {
        if (!r.isActive) return <Tag>inactive</Tag>;
        if (r.pausedFrom) {
          const from = dayjs(r.pausedFrom).format('DD MMM');
          const until = r.pausedUntil ? dayjs(r.pausedUntil).format('DD MMM') : '?';
          return (
            <Tag color="orange">
              paused {from}–{until}
            </Tag>
          );
        }
        return <Tag color="green">active</Tag>;
      },
    },
    {
      title: 'Materialized',
      key: 'materialized',
      align: 'right',
      render: (_, r) => r._count?.orders ?? 0,
    },
  ];

  return (
    <Card size="small" bordered={false} bodyStyle={{ padding: 0 }}>
      <div style={{ padding: '12px 12px 0', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <Segmented
          size="small"
          value={view}
          onChange={setView}
          options={[
            { label: 'List', value: 'list', icon: <UnorderedListOutlined /> },
            { label: 'By day', value: 'byday', icon: <CalendarOutlined /> },
          ]}
        />
        {view === 'byday' && (
          <Space size={4}>
            <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Show</span>
            <Tag.CheckableTag checked={showPickups} onChange={(c) => setShow(c, showDeliveries)}>
              Pickups
            </Tag.CheckableTag>
            <Tag.CheckableTag checked={showDeliveries} onChange={(c) => setShow(showPickups, c)}>
              Deliveries
            </Tag.CheckableTag>
          </Space>
        )}
      </div>
      {view === 'byday' ? (
        <div style={{ padding: 12 }}>
          <RecurringWeeklyGrid schedules={schedules} showPickups={showPickups} showDeliveries={showDeliveries} />
        </div>
      ) : (
        <Table
          rowKey="id"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={schedules}
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
          locale={{ emptyText: 'No recurring schedules for your account yet' }}
        />
      )}
    </Card>
  );
}
